import { Box, Button, chakra, Text } from '@chakra-ui/react';
import AppCard from 'presentation/components/shared/card/AppCard';
import { FC } from 'react';
import CardInfoRow from 'presentation/components/shared/card/CardInfoRow';
import { useTranslation } from 'react-i18next';
import useCurrencyText from 'hooks/shared/useCurrencyText';
import moment from 'moment';
import { useNavigate } from 'react-router-dom';
import AppRoute from 'router/AppRoute';
import { PeOrderOperationType } from 'enums/modules/pe/peOperationType';
import { PeOrderListItem } from 'model/modules/pe/orders/peOrderList';
import usePeOrderStatusText from 'hooks/modules/services/pe/usePeOrderStatusText';
import PeCompanyGridShortHeader from 'presentation/components/modules/pe/shared/grid/PeCompanyGridShortHeader';

interface Props {
  peOrder: PeOrderListItem;
  onCancelClick: () => void;
  showHeader?: boolean;
  disableNavigating?: boolean;
}

const PeSelfOrdersGridItem: FC<Props> = ({
  peOrder,
  onCancelClick,
  showHeader = true,
  disableNavigating = false,
  ...props
}) => {
  const { t } = useTranslation();

  const currencyText = useCurrencyText();

  const navigate = useNavigate();

  const getStatusText = usePeOrderStatusText();

  return (
    <AppCard
      overflow="hidden"
      cursor={disableNavigating ? undefined : 'pointer'}
      onClick={() => {
        if (disableNavigating) return;
        navigate(AppRoute.peSelfOrderDetail(peOrder.id));
      }}
      display="flex"
      flexDir="column"
      {...props}
    >
      {showHeader && <PeCompanyGridShortHeader company={peOrder.company} />}
      <Box
        flex="1"
        display="flex"
        flexDir="column"
        justifyContent="space-between"
        pt="5px"
        pb="20px"
        px="20px"
      >
        <Box>
          <CardInfoRow
            label={
              peOrder.operationType === PeOrderOperationType.Buy
                ? t('buyOrderRequest')
                : t('sellOrderRequest')
            }
            labelProps={{
              fontWeight: 700,
            }}
          />
          <CardInfoRow label={t('status')}>
            {getStatusText(peOrder.status)}
          </CardInfoRow>
          <CardInfoRow label={t('pePrice')}>
            <Text>
              {currencyText(peOrder.company.price, {
                symbol: peOrder.company.currency.symbol,
              })}
            </Text>
          </CardInfoRow>
          <CardInfoRow label={t('peSharesQuantity')}>
            <Text>{peOrder.quantity}</Text>
          </CardInfoRow>
          {typeof peOrder.volume === 'number' && (
            <CardInfoRow label={t('peOfferPrice')}>
              <Text>
                {currencyText(peOrder.volume, {
                  symbol: peOrder.company.currency.symbol,
                })}
              </Text>
            </CardInfoRow>
          )}
          <CardInfoRow label={t('orderCreatedAt')}>
            <Text>{moment(peOrder.createdAt).format('L')}</Text>
          </CardInfoRow>
        </Box>
        <Button
          w="full"
          mt="15px"
          disabled={!peOrder.isAllowCancel}
          onClick={(e) => {
            e.stopPropagation();
            onCancelClick();
          }}
        >
          {t('cancelAction')}
        </Button>
      </Box>
    </AppCard>
  );
};

export default chakra(PeSelfOrdersGridItem);
